import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Plus, Search, Filter, Download, Edit, Trash2, Eye, User, Building2, Phone, Mail, MapPin } from 'lucide-react';
import { DISTRITOS_PORTUGAL } from '../../types';
import { useToast } from '../Toast';
import Modal, { ConfirmModal } from '../Modal';
import clientesService from '../../services/clientes';
import Table from '../ui/Table';
import Pagination from '../ui/Pagination';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Button from '../ui/Button';
import ClienteModal from './ClienteModal';
import ClienteDetails from './ClienteDetails';
const ClientesList = ({ onClienteSelect, selectionMode = false }) => {
    const { success, error: showError } = useToast();
    // Estado da lista
    const [clientes, setClientes] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 25,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
    });
    const [isLoading, setIsLoading] = useState(false);
    const [sortBy, setSortBy] = useState('nome');
    const [sortOrder, setSortOrder] = useState('asc');
    // Estado dos filtros
    const [filters, setFilters] = useState({
        search: '',
        tipoCliente: undefined,
        distrito: undefined,
        ativo: true
    });
    const [showFilters, setShowFilters] = useState(false);
    // Estado dos modais
    const [selectedCliente, setSelectedCliente] = useState();
    const [showClienteModal, setShowClienteModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [clienteToDelete, setClienteToDelete] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [clienteDetailsId, setClienteDetailsId] = useState(null);
    // Carregar clientes
    const loadClientes = async () => {
        setIsLoading(true);
        try {
            const response = await clientesService.getClientes({
                ...filters,
                page: pagination.page,
                limit: pagination.limit,
                sortBy,
                sortOrder
            });
            setClientes(response.data);
            setPagination(response.pagination);
        }
        catch (err) {
            showError('Erro ao carregar clientes', err.message);
        }
        finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        loadClientes();
    }, [pagination.page, pagination.limit, filters, sortBy, sortOrder]);
    // Handlers
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };
    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        }
        else {
            setSortBy(field);
            setSortOrder('asc');
        }
    };
    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, page }));
    };
    const handlePageSizeChange = (limit) => {
        setPagination(prev => ({ ...prev, limit, page: 1 }));
    };
    const handleCreateCliente = () => {
        setSelectedCliente(undefined);
        setShowClienteModal(true);
    };
    const handleEditCliente = (cliente) => {
        setSelectedCliente(cliente);
        setShowClienteModal(true);
    };
    const handleDeleteCliente = (cliente) => {
        setClienteToDelete(cliente);
        setShowDeleteModal(true);
    };
    const handleViewDetails = (cliente) => {
        setClienteDetailsId(cliente.id);
        setShowDetailsModal(true);
    };
    const confirmDelete = async () => {
        if (!clienteToDelete)
            return;
        try {
            await clientesService.deleteCliente(clienteToDelete.id);
            success('Cliente removido com sucesso');
            loadClientes();
        }
        catch (err) {
            showError('Erro ao remover cliente', err.message);
        }
    };
    const handleClienteSave = (cliente) => {
        loadClientes();
    };
    const handleRowClick = (cliente) => {
        if (selectionMode && onClienteSelect) {
            onClienteSelect(cliente);
        }
    };
    // Colunas da tabela
    const columns = [
        {
            key: 'nome',
            title: 'Cliente',
            sortable: true,
            render: (value, cliente) => (_jsxs("div", { className: "flex items-center space-x-3", children: [_jsx("div", { className: `
            flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-white text-sm font-medium
            ${cliente.tipoCliente === 'EMPRESA' ? 'bg-blue-500' : 'bg-green-500'}
          `, children: cliente.tipoCliente === 'EMPRESA' ? (_jsx(Building2, { className: "h-4 w-4" })) : (_jsx(User, { className: "h-4 w-4" })) }), _jsxs("div", { children: [_jsx("div", { className: "font-medium text-gray-900", children: value }), cliente.nomeComercial && (_jsx("div", { className: "text-sm text-gray-500", children: cliente.nomeComercial }))] })] }))
        },
        {
            key: 'tipoCliente',
            title: 'Tipo',
            sortable: true,
            render: (value) => (_jsx("span", { className: `
          inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
          ${value === 'EMPRESA'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-green-100 text-green-800'}
        `, children: value === 'EMPRESA' ? 'Empresa' : 'Individual' }))
        },
        {
            key: 'contacto',
            title: 'Contacto',
            render: (value, cliente) => (_jsxs("div", { className: "space-y-1", children: [cliente.telefonePrincipal && (_jsxs("div", { className: "flex items-center text-sm text-gray-600", children: [_jsx(Phone, { className: "h-3 w-3 mr-1" }), cliente.telefonePrincipal] })), cliente.email && (_jsxs("div", { className: "flex items-center text-sm text-gray-600", children: [_jsx(Mail, { className: "h-3 w-3 mr-1" }), cliente.email] }))] }))
        },
        {
            key: 'localizacao',
            title: 'Localização',
            render: (value, cliente) => (_jsx("div", { className: "text-sm text-gray-600", children: cliente.cidade && cliente.distrito && (_jsxs("div", { className: "flex items-center", children: [_jsx(MapPin, { className: "h-3 w-3 mr-1" }), cliente.cidade, ", ", cliente.distrito] })) }))
        },
        {
            key: 'stats',
            title: 'Equipamentos',
            align: 'center',
            render: (value, cliente) => (_jsx("div", { className: "text-center", children: _jsx("span", { className: "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800", children: cliente._count?.equipamentos || 0 }) }))
        },
        {
            key: 'actions',
            title: 'Ações',
            align: 'center',
            render: (value, cliente) => (_jsxs("div", { className: "flex items-center justify-center space-x-2", children: [_jsx(Button, { size: "xs", variant: "ghost", onClick: (e) => {
                            e.stopPropagation();
                            handleViewDetails(cliente);
                        }, children: _jsx(Eye, { className: "h-4 w-4" }) }), _jsx(Button, { size: "xs", variant: "ghost", onClick: (e) => {
                            e.stopPropagation();
                            handleEditCliente(cliente);
                        }, children: _jsx(Edit, { className: "h-4 w-4" }) }), _jsx(Button, { size: "xs", variant: "ghost", onClick: (e) => {
                            e.stopPropagation();
                            handleDeleteCliente(cliente);
                        }, children: _jsx(Trash2, { className: "h-4 w-4 text-danger-600" }) })] }))
        }
    ];
    const tipoClienteOptions = [
        { value: '', label: 'Todos os tipos' },
        { value: 'INDIVIDUAL', label: 'Pessoa Individual' },
        { value: 'EMPRESA', label: 'Empresa' }
    ];
    const distritoOptions = [
        { value: '', label: 'Todos os distritos' },
        ...DISTRITOS_PORTUGAL.map(distrito => ({
            value: distrito,
            label: distrito
        }))
    ];
    const ativoOptions = [
        { value: '', label: 'Todos' },
        { value: 'true', label: 'Ativos' },
        { value: 'false', label: 'Inativos' }
    ];
    return (_jsxs("div", { className: "space-y-6", children: [_jsxs("div", { className: "flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-2xl font-bold text-gray-900", children: "Clientes" }), _jsx("p", { className: "text-gray-600", children: "Gerir clientes individuais e empresas" })] }), !selectionMode && (_jsxs("div", { className: "flex space-x-3", children: [_jsx(Button, { variant: "secondary", leftIcon: _jsx(Download, { className: "h-4 w-4" }), children: "Exportar" }), _jsx(Button, { variant: "primary", leftIcon: _jsx(Plus, { className: "h-4 w-4" }), onClick: handleCreateCliente, children: "Novo Cliente" })] }))] }), _jsxs("div", { className: "bg-white p-4 rounded-lg border border-gray-200 space-y-4", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx("div", { className: "flex-1 max-w-md", children: _jsx(Input, { placeholder: "Pesquisar por nome, email, telefone, NIF...", leftIcon: _jsx(Search, { className: "h-4 w-4" }), value: filters.search, onChange: (e) => handleFilterChange('search', e.target.value) }) }), _jsx(Button, { variant: "ghost", size: "sm", leftIcon: _jsx(Filter, { className: "h-4 w-4" }), onClick: () => setShowFilters(!showFilters), children: "Filtros" })] }), showFilters && (_jsxs("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200", children: [_jsx(Select, { label: "Tipo de Cliente", options: tipoClienteOptions, value: filters.tipoCliente || '', onChange: (e) => handleFilterChange('tipoCliente', e.target.value || undefined) }), _jsx(Select, { label: "Distrito", options: distritoOptions, value: filters.distrito || '', onChange: (e) => handleFilterChange('distrito', e.target.value || undefined) }), _jsx(Select, { label: "Estado", options: ativoOptions, value: filters.ativo === undefined ? '' : filters.ativo.toString(), onChange: (e) => {
                                    const value = e.target.value;
                                    handleFilterChange('ativo', value === '' ? undefined : value === 'true');
                                } })] }))] }), _jsx(Table, { data: clientes, columns: columns, keyField: "id", isLoading: isLoading, emptyMessage: "Nenhum cliente encontrado", sortBy: sortBy, sortOrder: sortOrder, onSort: handleSort, onRowClick: selectionMode ? handleRowClick : undefined, hoverable: true, striped: true }), _jsx(Pagination, { pagination: pagination, onPageChange: handlePageChange, onPageSizeChange: handlePageSizeChange }), _jsx(ClienteModal, { isOpen: showClienteModal, onClose: () => setShowClienteModal(false), cliente: selectedCliente, onSave: handleClienteSave }), _jsx(ConfirmModal, { isOpen: showDeleteModal, onClose: () => setShowDeleteModal(false), onConfirm: confirmDelete, title: "Remover Cliente", message: `Tem certeza que deseja remover o cliente "${clienteToDelete?.nome}"? Esta ação não pode ser desfeita.`, confirmText: "Remover", type: "danger" }), _jsx(Modal, { isOpen: showDetailsModal, onClose: () => {
                    setShowDetailsModal(false);
                    setClienteDetailsId(null);
                }, size: "xl", title: "Detalhes do Cliente", children: clienteDetailsId && (_jsx(ClienteDetails, { clienteId: clienteDetailsId, onEdit: () => {
                        setShowDetailsModal(false);
                        const cliente = clientes.find(c => c.id === clienteDetailsId);
                        if (cliente) {
                            handleEditCliente(cliente);
                        }
                    } })) })] }));
};
export default ClientesList;
